import {fromSeconds} from 'common/date';
import * as React from 'react';
import {AccountV2Icon} from 'src/icons/AccountV2Icon';
import {logoUrl} from 'src/logoOptions';
import type {
  AttachmentCardBenefitFragment,
  AttachmentFeePeriodFragment,
} from '@transactions/graphql';

const formatCents = (cents: number): string => {
  const dollars = Math.abs(cents) / 100;
  return `$${dollars.toFixed(2)}`;
};

const formatDateRange = (startDate: number, endDate: number): string => {
  const start = fromSeconds(startDate);
  // Half-open bounds: the displayed end month is the last covered day's
  const end = fromSeconds(endDate).minus({days: 1});
  const fmt = {month: 'short' as const, year: 'numeric' as const};
  return `${start.toLocaleString(fmt)} \u2013 ${end.toLocaleString(fmt)}`;
};

export const totalRemaining = (benefit: AttachmentCardBenefitFragment): number =>
  benefit.periods.reduce((sum, p) => sum + p.remainingAmount, 0);

export const totalAmount = (benefit: AttachmentCardBenefitFragment): number =>
  benefit.periods.reduce((sum, p) => sum + p.amount, 0);

// Benefit periods are the eligibility bounds, not the fee period dates:
// calendar-aligned schedules cover whole calendar units, which extend
// outside an off-calendar fee period (a Feb 9 card year's monthly benefit
// starts Feb 1, and its last quarter ends before the fee period does).
export const benefitCoversDate = (
  benefit: AttachmentCardBenefitFragment,
  date: number,
): boolean =>
  benefit.periods.some((p) => date >= p.startDate && date < p.endDate);

const FeePeriodGroup = ({
  feePeriod,
  transactionDate,
  onSelect,
}: {
  feePeriod: AttachmentFeePeriodFragment;
  transactionDate?: number;
  onSelect: (benefit: AttachmentCardBenefitFragment, feePeriod: AttachmentFeePeriodFragment) => void;
}) => (
  <div className="flex flex-col gap-1">
    <div className="text-muted-foreground flex items-center justify-between gap-2 text-xs">
      <span className="flex min-w-0 items-center gap-1.5">
        {feePeriod.account.logo ? (
          <img
            src={logoUrl(feePeriod.account.logo)}
            alt=""
            className="size-4 shrink-0 rounded-sm"
          />
        ) : (
          <AccountV2Icon account={feePeriod.account} className="size-4 shrink-0" />
        )}
        <span className="truncate font-medium">
          {feePeriod.payee ?? feePeriod.account.name}
        </span>
      </span>
      <span className="shrink-0">
        {formatDateRange(feePeriod.startDate, feePeriod.endDate)}
      </span>
    </div>
    {feePeriod.benefits.map((benefit) => {
      const remaining = totalRemaining(benefit);
      const total = totalAmount(benefit);
      const fullyUsed = remaining <= 0 && total > 0;
      const outOfPeriod =
        transactionDate != null && !benefitCoversDate(benefit, transactionDate);
      return (
        <button
          key={benefit._id}
          type="button"
          onClick={() => onSelect(benefit, feePeriod)}
          title={
            outOfPeriod
              ? 'No period of this benefit covers the transaction date'
              : undefined
          }
          className={`border-border hover:bg-muted/50 flex w-full items-center justify-between gap-2 rounded-md border p-2.5 text-left ${fullyUsed || outOfPeriod ? 'opacity-50' : ''}`}
        >
          <span className="text-sm font-medium">{benefit.name}</span>
          <span className="text-muted-foreground shrink-0 text-xs">
            {formatCents(remaining)} / {formatCents(total)}
          </span>
        </button>
      );
    })}
  </div>
);

export const BenefitPicker = ({
  feePeriods,
  transactionDate,
  accountId,
  lockAccount = false,
  onSelect,
}: {
  feePeriods: AttachmentFeePeriodFragment[];
  transactionDate?: number;
  /** Restrict to benefits reachable from this account (see benefitOnAccount) */
  accountId?: string;
  /**
   * Keep the account restriction even under "Show all benefits", so the
   * escape hatch widens dates only. The bulk edit modal sets this: one pick
   * links every selected row, so a cross-account benefit is never right
   * there, while the single-row flows keep the full escape hatch.
   */
  lockAccount?: boolean;
  onSelect: (benefit: AttachmentCardBenefitFragment, feePeriod: AttachmentFeePeriodFragment) => void;
}) => {
  const [showAll, setShowAll] = React.useState(false);

  const periodsWithBenefits = feePeriods.filter(
    (fp) => fp.benefits.length > 0,
  );

  // Each benefit lands on exactly one account, and which one depends on the
  // benefit rather than its fee period. A benefit that loads value elsewhere
  // (Uber Cash) is recorded there — createCardBenefit rejects the card as a
  // credit destination precisely because a card's own statement credits
  // arrive through its feed, so recording synthetic ones would double them.
  // The rest refund on the card. Hence per benefit, not per fee period: a
  // card charge and an Uber Cash spend select disjoint sets out of the same
  // fee period group.
  const accountPeriods = React.useMemo(() => {
    if (!accountId) return periodsWithBenefits;
    return periodsWithBenefits
      .map((fp) => ({
        ...fp,
        benefits: fp.benefits.filter(
          (b) => (b.creditAccountId ?? fp.account._id) === accountId,
        ),
      }))
      .filter((fp) => fp.benefits.length > 0);
  }, [periodsWithBenefits, accountId]);

  // What "Show all benefits" reveals: normally everything, but a caller
  // that locks the account keeps that restriction and only widens dates.
  const showAllPool = lockAccount ? accountPeriods : periodsWithBenefits;

  // Filter to fee periods with at least one benefit period covering the
  // transaction date (benefit periods, not fee period dates — see
  // benefitCoversDate)
  const matchingPeriods = React.useMemo(() => {
    if (showAll) return showAllPool;
    if (!transactionDate) return accountPeriods;
    return accountPeriods.filter((fp) =>
      fp.benefits.some((b) => benefitCoversDate(b, transactionDate)),
    );
  }, [showAllPool, accountPeriods, transactionDate, showAll]);

  // Counted over benefits, not fee periods: the account filter can narrow a
  // group's benefits while leaving the group itself on screen.
  const benefitCount = (periods: AttachmentFeePeriodFragment[]): number =>
    periods.reduce((sum, fp) => sum + fp.benefits.length, 0);
  const hasHidden =
    !showAll && benefitCount(matchingPeriods) < benefitCount(showAllPool);

  if (periodsWithBenefits.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">No benefits found.</p>
    );
  }

  if (matchingPeriods.length === 0 && !showAll) {
    return (
      <div className="flex flex-col items-center gap-2 py-4">
        <p className="text-muted-foreground text-sm">
          {accountPeriods.length === 0
            ? 'No benefits on this account.'
            : 'No benefits cover this transaction date.'}
        </p>
        {/* Offer the escape hatch only when it would reveal something — a
            locked account with no benefits has nothing behind the button. */}
        {benefitCount(showAllPool) > 0 && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="text-sm font-medium underline"
          >
            Show all benefits
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {matchingPeriods.map((fp) => (
        <FeePeriodGroup
          key={fp._id}
          feePeriod={fp}
          transactionDate={transactionDate}
          onSelect={onSelect}
        />
      ))}
      {hasHidden && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="text-muted-foreground text-xs underline"
        >
          Show all benefits
        </button>
      )}
    </div>
  );
};
